import { add, getRate, multiplyWithRate, round, subtract, sum, } from "@pentacode/openapi";
import { getAutomaticBreak, getStatutoryBreak } from "./breaks";
import { getSalaryOrDefault } from "./contract.util";
import { Localized } from "./localized";
import { absenceTypes, BreakMode, EmploymentType, TimeEntryType } from "./model";
import { getProratedPaymentForRange } from "./payroll";
import { getMinimumHourlyRate } from "./salary";
import { hour } from "./time";
import { dateAdd, getAllContractsForRange, getContractForDate, getNextDay, getTimeBetweenDates, getTimeSettingsForEntry, hoursToMilliseconds, } from "./utils";
export var IssueType;
(function (IssueType) {
    /** Two shifts exists on the same day / employee with overlapping times (either planned or completed) */
    IssueType[IssueType["OverlappingShifts"] = 0] = "OverlappingShifts";
    /** The employee has exceeded the legally allowed daily work time (10 hours by default) */
    IssueType[IssueType["ExcessiveWorkDay"] = 1] = "ExcessiveWorkDay";
    /** There is an insufficent time gap between the last shift of one day and the first shift of the next (legal minimum time gap is 11 hours) */
    IssueType[IssueType["DeficientIdlePeriod"] = 2] = "DeficientIdlePeriod";
    /** The recorded break duration is lower than the legal minimum based on the shift duration */
    IssueType[IssueType["DeficientBreak"] = 3] = "DeficientBreak";
    /**
     * The employees effective hourly rate in the given payroll period
     * (([salary] + [other wages]) / [worked hours]) is below the legal threshold
     */
    IssueType[IssueType["DeficientWages"] = 4] = "DeficientWages";
    /** The employe has exceeded their legal wage limit */
    IssueType[IssueType["ExcessiveWages"] = 5] = "ExcessiveWages";
    /** No actual start time has been recorded for a planned shift even after the planned time has passed */
    IssueType[IssueType["MissingShiftStart"] = 6] = "MissingShiftStart";
    /** No actual end time has been recorded for a planned shift even after the planned time has passed */
    IssueType[IssueType["MissingShiftEnd"] = 7] = "MissingShiftEnd";
    /** No contract exists for the shift date */
    IssueType[IssueType["MissingContract"] = 8] = "MissingContract";
    /** A daily revenue report has not been completed */
    IssueType[IssueType["PendingDailyRevenues"] = 9] = "PendingDailyRevenues";
    /** The daily revenue report has conflicting values for the expected / actual cash balance */
    IssueType[IssueType["UnbalancedDailyCash"] = 10] = "UnbalancedDailyCash";
})(IssueType || (IssueType = {}));
export function getIssueMessage(type, country = "DE") {
    switch (type) {
        case IssueType.OverlappingShifts:
            return "Schichtüberlappung";
        case IssueType.ExcessiveWorkDay:
            return `Arbeitszeit > ${Localized[country].maxWorkingHoursPerDay} Std.`;
        case IssueType.DeficientIdlePeriod:
            return `Ruhezeit < ${Localized[country].minRestingHours} Std.`;
        case IssueType.DeficientBreak:
            return "Unterschreitung ges. Pause";
        case IssueType.DeficientWages:
            return "Mindestlohnunterschr.";
        case IssueType.ExcessiveWages:
            return "Überschr. Lohngrenze";
        case IssueType.MissingShiftStart:
            return "Schicht Nicht Angetr.";
        case IssueType.MissingShiftEnd:
            return "Schicht Nicht Beendet";
        case IssueType.MissingContract:
            return "Außerh. Vertragszeitr.";
        case IssueType.PendingDailyRevenues:
            return "Tagesabrechnung nicht abgeschlossen";
        case IssueType.UnbalancedDailyCash:
            return "Fehlerhafte Tagesabrechnung";
    }
}
export const ShiftIssueTypes = [
    IssueType.OverlappingShifts,
    IssueType.ExcessiveWorkDay,
    IssueType.DeficientIdlePeriod,
    IssueType.DeficientBreak,
    IssueType.MissingShiftStart,
    IssueType.MissingShiftEnd,
    IssueType.MissingContract,
];
export const PayrollIssueTypes = [IssueType.DeficientWages, IssueType.ExcessiveWages];
/**
 * These are issue types related to employees (shift- and payroll-related issues)
 */
export const EmployeeIssueTypes = [...ShiftIssueTypes, ...PayrollIssueTypes];
/**
 * These are issue types related to revenue (daily revenue) reports
 */
export const RevenueIssueTypes = [IssueType.PendingDailyRevenues, IssueType.UnbalancedDailyCash];
function timeEntryToShiftDetails(entry) {
    return {
        id: entry.id,
        type: entry.type,
        date: entry.date,
        start: entry.startFinal?.toISOString() || entry.startPlanned?.toISOString() || null,
        end: entry.endFinal?.toISOString() || entry.endPlanned?.toISOString() || null,
        duration: entry.duration,
        break: entry.break || 0,
        employeeId: entry.employeeId,
        positionId: entry.positionId,
    };
}
/**
 * Get all issues related to shifts. Includes:
 *
 *   - IssueType.OverlappingShifts,
 *   - IssueType.ExcessiveWorkDay,
 *   - IssueType.DeficientIdlePeriod,
 *   - IssueType.DeficientBreak,
 *   - IssueType.MissingShiftStart,
 *   - IssueType.MissingShiftEnd,
 *   - IssueType.MissingContract,
 */
export function getShiftIssues(employee, settings, company, timeEntries, { from, to }) {
    const issues = [];
    const now = new Date();
    const { maxWorkingHoursPerDay, minRestingHours } = Localized[company.country];
    const commitBefore = settings.commitTimeEntriesBefore;
    if (commitBefore && commitBefore > from) {
        from = commitBefore;
    }
    const fromIncludingPreviousDay = dateAdd(from, { days: -1 });
    timeEntries = timeEntries
        .filter((entry) => !entry.deleted &&
        entry.employeeId === employee.id &&
        entry.type === TimeEntryType.Work &&
        // Include time entries from the last day before the current period to check for shift overlaps
        entry.date >= fromIncludingPreviousDay &&
        entry.date < to &&
        // Only fully planned, or at least started shifts
        (entry.planned || entry.startFinal))
        .sort((a, b) => a.start.getTime() - b.start.getTime());
    for (let i = 0, dayEntries = []; i < timeEntries.length; i++) {
        const curr = timeEntries[i];
        const next = timeEntries[i + 1];
        dayEntries.push(curr);
        // Last shift of the day
        if (!next || next.start.toDateString() !== curr.start.toDateString()) {
            // Check if total work duration on this day exceeds 10 hours
            const totalDur = dayEntries.reduce((total, timeEntry) => {
                if (!(timeEntry.startPlanned || timeEntry.startFinal) ||
                    !(timeEntry.endPlanned || timeEntry.endFinal)) {
                    // not enough information to calculate a duration
                    return total;
                }
                const timeSettings = getTimeSettingsForEntry(settings.availableTimeSettings, company, timeEntry);
                // TODO: we get this to subtract it from the total duration, so we should use the common break calculation function here
                // Probably using the mixed mode, since we want to evaluate if past as well as future planned shifts are compliant
                const brk = timeEntry.final
                    ? timeEntry.break || 0
                    : [BreakMode.Planned, BreakMode.PlannedPlusManual].includes(timeSettings.breakMode)
                        ? timeEntry.breakPlanned || 0
                        : getAutomaticBreak(timeEntry.duration, timeSettings);
                const durationMs = getTimeBetweenDates(timeEntry.start, timeEntry.end);
                const breakMs = hoursToMilliseconds(brk);
                const finalDuration = subtract(durationMs, breakMs);
                return add(total, finalDuration);
            }, 0);
            const maxDuration = hoursToMilliseconds(maxWorkingHoursPerDay);
            if (totalDur > maxDuration) {
                issues.push({
                    type: IssueType.ExcessiveWorkDay,
                    date: dayEntries[0].date,
                    details: {
                        employeeId: curr.employeeId,
                        shifts: dayEntries.map((entry) => timeEntryToShiftDetails(entry)),
                        totalDuration: totalDur,
                        maxDuration,
                    },
                    ignored: dayEntries.some((e) => e.ignoreIssues?.includes(IssueType.ExcessiveWorkDay)),
                });
            }
            if (curr.end && next && next.start) {
                const idleDuration = getTimeBetweenDates(curr.end, next.start);
                const minIdleDuration = hoursToMilliseconds(minRestingHours);
                if (idleDuration < minIdleDuration) {
                    issues.push({
                        type: IssueType.DeficientIdlePeriod,
                        date: next.date,
                        details: {
                            employeeId: curr.employeeId,
                            shifts: [timeEntryToShiftDetails(curr), timeEntryToShiftDetails(next)],
                            idleDuration,
                            minIdleDuration,
                        },
                        ignored: [curr, next].some((e) => e.ignoreIssues?.includes(IssueType.DeficientIdlePeriod)),
                    });
                }
            }
            dayEntries = [];
        }
        // Check if the shift was not started yet and we are past the start window
        if (!curr.startFinal) {
            let lateStart;
            if (curr.startPlanned) {
                // Employee is late 1 hour after planned start
                lateStart = new Date(curr.startPlanned.getTime() + 1 * hour);
            }
            // TODO: With the current filter for timeEntries, this branch is probably never reached
            // since entries either are planned (have startPlanned) or have been started (have startFinal)
            else if (curr.endPlanned) {
                // Employee is late after the planned end
                lateStart = curr.endPlanned;
            }
            else {
                // Employee is late at the start of the next day
                lateStart = new Date(getNextDay(curr.date));
            }
            if (now > lateStart) {
                issues.push({
                    type: IssueType.MissingShiftStart,
                    date: curr.date,
                    details: {
                        employeeId: curr.employeeId,
                        shift: timeEntryToShiftDetails(curr),
                    },
                    ignored: curr.ignoreIssues?.includes(IssueType.MissingShiftStart),
                });
            }
        }
        // Check if started but not finished while past the latest checkout time
        if (curr.startFinal) {
            const timeSettings = getTimeSettingsForEntry(settings.availableTimeSettings, company, curr);
            const latestCheckout = new Date(curr.startFinal.getTime() + timeSettings.checkoutWindow * hour);
            if (!curr.endFinal && now > latestCheckout) {
                issues.push({
                    type: IssueType.MissingShiftEnd,
                    date: curr.date,
                    details: {
                        employeeId: curr.employeeId,
                        shift: timeEntryToShiftDetails(curr),
                    },
                    ignored: curr.ignoreIssues?.includes(IssueType.MissingShiftEnd),
                });
            }
        }
        // Check for overlapping shifts on the same day
        if (next && curr.end && next.start && curr.end > next.start) {
            issues.push({
                type: IssueType.OverlappingShifts,
                date: next.date,
                details: {
                    employeeId: curr.employeeId,
                    shifts: [timeEntryToShiftDetails(curr), timeEntryToShiftDetails(next)],
                },
                ignored: [curr, next].some((e) => e.ignoreIssues?.includes(IssueType.OverlappingShifts)),
            });
        }
        // Check if the recorded break duration is lower than the legal minimum based on the shift duration
        const statutoryBreak = getStatutoryBreak(curr.duration, settings);
        if (curr.final && (curr.break || 0) < statutoryBreak) {
            issues.push({
                type: IssueType.DeficientBreak,
                date: curr.date,
                details: {
                    employeeId: curr.employeeId,
                    shift: timeEntryToShiftDetails(curr),
                    minBreak: statutoryBreak,
                },
                ignored: curr.ignoreIssues?.includes(IssueType.DeficientBreak),
            });
        }
        // Check if a contract exists for the shift date
        if (!getContractForDate(employee.contracts, curr.date)) {
            issues.push({
                date: curr.date,
                type: IssueType.MissingContract,
                details: {
                    shift: timeEntryToShiftDetails(curr),
                    employeeId: curr.employeeId,
                },
                ignored: curr.ignoreIssues?.includes(IssueType.MissingContract),
            });
        }
    }
    return issues.filter((issue) => issue.date >= from);
}
/**
 *  Extracts the shift details from an issue for those that have any
 */
export function getShiftsForIssue(issue) {
    if ("shifts" in issue.details) {
        return issue.details.shifts;
    }
    else if ("shift" in issue.details) {
        return [issue.details.shift];
    }
    else {
        return [];
    }
}
/**
 * Get all payroll-related issues this includes
 *
 *   - IssueType.ExcessiveWages,
 *   - IssueType.DeficientWages,
 */
export function getPayrollIssues(settings, company, employee, timeEntries, { from, to }) {
    const issues = [];
    const deficientWagesIssue = checkDeficientWages(settings, company, employee, timeEntries, { from, to });
    const excessiveWagesIssue = checkExcessiveWages(settings, company, employee, timeEntries, { from, to });
    if (deficientWagesIssue) {
        issues.push(deficientWagesIssue);
    }
    if (excessiveWagesIssue) {
        issues.push(excessiveWagesIssue);
    }
    return issues;
}
/**
 * Calculates the total pay applicable to determining whether an employee has received the minimum
 * effective hourly rate or exceeded the maximum wage limit. No all wage types are included in this.
 * Included are:
 *
 * - Salaries
 * - Benefits, if `includeInMinimumWageComparison = true`
 * - Hourly wages (including absences and paid breaks)
 * - Taxed bonuses (bonuses from absences or those exceeding the legal limit (25€/hour))
 * - Commissions
 *
 * **Not** included are:
 *
 * - Taxfree bonuses
 * - Time/vacation bookings
 * - Advance pay
 */
function getPayRelevantToPayrollIssues(settings, company, allContracts, timeEntries, { from, to }) {
    // Only include time entries in the given time entries that have a result
    const relevantTimeEntries = timeEntries.filter((timeEntry) => [TimeEntryType.Work, ...absenceTypes].includes(timeEntry.type) &&
        timeEntry.date >= from &&
        timeEntry.date < to &&
        timeEntry.result);
    const contracts = getAllContractsForRange(allContracts, { from, to });
    const benefits = contracts
        .flatMap((c) => {
        return c.benefits.flatMap((benefit) => {
            return getProratedPaymentForRange(c, { from, to }, benefit, settings.startPayrollPeriod).map((interval) => ({
                amount: interval.amount,
                benefit,
            }));
        });
    })
        // Only include beneftis where `includeInMinimumWageComparison = true`
        .filter(({ benefit }) => {
        const benefitType = company.benefitTypes.find((bt) => bt.id === benefit.typeId);
        return benefitType?.includeInMinimumWageComparison;
    })
        .reduce((sum, benefit) => add(sum, benefit.amount), 0);
    const salaries = contracts
        .flatMap((c) => {
        return c.salaries
            .filter((s) => s.type === "monthly")
            .flatMap((salary) => {
            return getProratedPaymentForRange(c, { from, to }, salary, settings.startPayrollPeriod).map((interval) => ({
                amount: interval.amount,
                salary,
            }));
        });
    })
        .reduce((sum, salary) => add(sum, salary.amount), 0);
    const hourlyWages = relevantTimeEntries
        .filter((timeEntry) => {
        const contract = getContractForDate(contracts, timeEntry.date);
        if (!contract) {
            return false;
        }
        return getSalaryOrDefault(timeEntry.positionId, contract.salaries)?.type === "hourly";
    })
        .reduce((total, timeEntry) => add(total, multiplyWithRate(round(add(timeEntry.result.base.duration, timeEntry.result.breaks.paidDuration), 2), timeEntry.result.base.hourlyRate)), 0);
    const taxedBonuses = relevantTimeEntries.reduce((total, r) => sum(total, r.result.bonuses.filter((b) => !b.taxFree).reduce((sum, b) => add(sum, b.wages), 0)), 0);
    const commissions = relevantTimeEntries.reduce((total, r) => sum(total, r.result.commission?.wages || 0), 0);
    return sum(salaries, benefits, hourlyWages, taxedBonuses, commissions);
}
/** Checks whether the employee has received the legal minimum effective hourly rate */
function checkDeficientWages(settings, company, employee, timeEntries, { from, to }) {
    const date = dateAdd(to, { days: -1 });
    const contracts = getAllContractsForRange(employee.contracts, { from, to });
    const contractAtPeriodEnd = getContractForDate(employee.contracts, date);
    // Interns, Trainees and Dual Students are not subject to minimum wage laws.
    // Also if an annual time sheet is kept for the employee, we don't need to check for minimum wage
    const checkMinimumWage = contracts.some((c) => c.employmentType !== EmploymentType.Intern &&
        c.employmentType !== EmploymentType.Trainee &&
        c.employmentType !== EmploymentType.DualStudent &&
        !c.annualTimeSheet);
    if (!contractAtPeriodEnd || !checkMinimumWage) {
        return null;
    }
    const wages = getPayRelevantToPayrollIssues(settings, company, employee.contracts, timeEntries, { from, to });
    const hours = timeEntries
        .filter((timeEntry) => [TimeEntryType.Work, ...absenceTypes].includes(timeEntry.type) &&
        timeEntry.date >= from &&
        timeEntry.date < to)
        .reduce((total, timeEntry) => sum(total, timeEntry.result?.base.duration || 0, timeEntry.result?.breaks.paidDuration || 0), 0);
    if (!wages || !hours) {
        return null;
    }
    const effectiveHourlyRate = round(getRate(round(wages, 2), round(hours, 2)), 2);
    const minHourlyRate = getMinimumHourlyRate(from);
    // We're giving a little bit of wiggle room here to account for rounding errors
    if (checkMinimumWage && effectiveHourlyRate - minHourlyRate < -0.01) {
        return {
            type: IssueType.DeficientWages,
            date,
            details: {
                employeeId: employee.id,
                hours,
                wages,
                minHourlyRate,
                effectiveHourlyRate,
            },
            ignored: contractAtPeriodEnd.ignoreIssues.some((issue) => issue.type === IssueType.DeficientWages && issue.date === date),
        };
    }
    return null;
}
/** Checks whether the employee has exceeded their wage limit (only applicable to marginal employees and "midi jobbers") */
function checkExcessiveWages(settings, company, employee, timeEntries, { from, to }) {
    const date = dateAdd(to, { days: -1 });
    const contracts = getAllContractsForRange(employee.contracts, { from, to });
    const contractAtPeriodEnd = getContractForDate(employee.contracts, date);
    if (!contractAtPeriodEnd) {
        return null;
    }
    const maxWages = contracts.find((c) => [EmploymentType.Marginal, EmploymentType.MidiJob].includes(c.employmentType) && c.maxSalary)?.maxSalary;
    if (!maxWages) {
        return null;
    }
    const wages = getPayRelevantToPayrollIssues(settings, company, employee.contracts, timeEntries, { from, to });
    if (wages && maxWages && wages > maxWages) {
        return {
            type: IssueType.ExcessiveWages,
            date,
            details: {
                employeeId: employee.id,
                wages,
                maxWages,
            },
            ignored: contractAtPeriodEnd.ignoreIssues.some((issue) => issue.type === IssueType.ExcessiveWages && issue.date === date),
        };
    }
    return null;
}
const issueTypeKeyMap = new Map(Object.entries(IssueType).map(([key, value]) => [value, key]));
export function toIssueTypeKey(issueType) {
    const key = issueTypeKeyMap.get(issueType);
    if (key) {
        return key;
    }
    throw new Error(`Invalid issue type: ${issueType}`);
}
//# sourceMappingURL=issues.js.map